import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useAppStore } from "@hooks/useAppStore";

/**
 * Decides whether the signed-in user still owes us onboarding, by checking the
 * app's configured `requiredFields` against their persisted onboarding profile.
 *
 * Note the two unrelated `onboarding` keys on the store: `state.onboarding` is
 * the configured step list, `state.app.onboarding` holds `requiredFields`.
 */
export const useOnboardingGate = () => {
	const {
		userInfo,
		isLoading: isAuthLoading,
		isFetchingUser,
	} = useAuthContext();
	const requiredFields = useAppStore(
		(state) => state.app.onboarding?.requiredFields,
	);
	const onboardingSteps = useAppStore((state) => state.onboarding);

	const hasRequiredFields = Boolean(requiredFields?.length);
	const profile = userInfo?.onboarding?.profile;

	// The applet renders only enabled steps, so an all-disabled config has no
	// flow to send anyone to.
	const hasOnboardingFlow = Boolean(
		onboardingSteps?.some((step) => step.enabled),
	);

	// `[]` is truthy and counts as answered; `""`, `undefined` and `null` do not.
	const isProfileComplete = (requiredFields ?? []).every((field) =>
		Boolean(profile?.[field]),
	);

	// Nothing to decide without requiredFields, so callers never wait on /users/me.
	// An in-flight fetch counts as undecided: the cached profile can be a render
	// behind a just-completed save, and acting on it would bounce the user back
	// into onboarding they have already finished.
	const isLoading = hasRequiredFields && (isAuthLoading || isFetchingUser);

	return {
		isLoading,
		hasOnboardingFlow,
		needsOnboarding:
			!isLoading &&
			!!userInfo &&
			hasOnboardingFlow &&
			hasRequiredFields &&
			!isProfileComplete,
	};
};
